import { tv, type VariantProps } from 'tailwind-variants'

import { dataFocusVisibleClasses } from '@/src/styles/classes'

export const cardVariants = tv({
  slots: {
    base: [
      'font-nunito',
      'flex',
      'flex-col',
      'relative',
      'web:overflow-hidden',
      'h-auto',
      'outline-none',
      'web:box-border',
      'bg-white',
      'transition',
      'duration-300',
      'ease-in-out',
      ...dataFocusVisibleClasses,
    ],
    header: [
      'font-nunito',
      'flex',
      'p-3',
      'z-10',
      'w-full',
      'justify-start',
      'text-left',
      'item-center',
      'shrink-0',
      'overflow-inherit',
      'color-inherit',
    ],
    body: [
      'font-nunito',
      'relative',
      'flex',
      'flex-1',
      'w-full',
      'p-3',
      'flex-auto',
      'flex-col',
      'place-content-inherit',
      'align-items-inherit',
      'h-auto',
      'break-words',
      'text-left',
      'overflow-y-auto',
    ],
    footer: ['p-3', 'h-auto', 'flex', 'w-full', 'text-left'],
  },
  variants: {
    shadow: {
      none: {
        base: 'shadow-none',
      },
      xs: {
        base: 'shadow-xs android:elevation-sm',
      },
      sm: {
        base: 'shadow-sm android:elevation-md',
      },
      md: {
        base: 'shadow-md android:elevation-lg',
      },
      lg: {
        base: 'shadow-lg android:elevation-xl',
      },
      xl: {
        base: 'shadow-xl android:elevation-2xl',
      },
    },
    shadowInner: {
      true: 'shadow-inner',
    },
    radius: {
      none: {
        header: 'rounded-none',
        footer: 'rounded-none',
        base: 'rounded-none',
      },
      sm: {
        header: 'rounded-t-sm',
        footer: 'rounded-b-sm',
        base: 'rounded-sm',
      },
      md: {
        header: 'rounded-t-md',
        footer: 'rounded-b-md',
        base: 'rounded-md',
      },
      lg: {
        header: 'rounded-t-lg',
        footer: 'rounded-b-lg',
        base: 'rounded-lg',
      },
      xl: {
        header: 'rounded-t-xl',
        footer: 'rounded-b-xl',
        base: 'rounded-xl',
      },
    },
    fullWidth: {
      true: {
        base: 'w-full',
      },
    },
    isHoverable: {
      true: {
        base: 'data-[hover=true]:translate-x-px data-[hover=true]:translate-y-px',
      },
    },
    isPressable: {
      true: { base: 'cursor-pointer' },
    },
    isBlurred: {
      true: {
        base: ['bg-default-light/50', 'backdrop-blur-md', 'backdrop-saturate-150'],
      },
    },
    isDisabled: {
      true: {
        base: 'cursor-not-allowed opacity-disabled',
      },
    },
  },
  compoundVariants: [
    {
      isHoverable: true,
      shadow: 'xs',
      class: 'data-[hover=true]:shadow-none',
    },
    {
      isHoverable: true,
      shadow: 'sm',
      class: 'data-[hover=true]:shadow-xs',
    },
    {
      isHoverable: true,
      shadow: 'md',
      class: 'data-[hover=true]:shadow-sm',
    },
    {
      isHoverable: true,
      shadow: 'lg',
      class: 'data-[hover=true]:shadow-md',
    },
    {
      isHoverable: true,
      isBlurred: true,
      class: 'data-[hover=true]:bg-default-bg-medium/50',
    },
    {
      isHoverable: true,
      isBlurred: false,
      class: 'data-[hover=true]:bg-default-bg-medium',
    },
  ],
  defaultVariants: {
    radius: 'lg',
    shadow: 'md',
    fullWidth: false,
    isHoverable: false,
    isPressable: false,
    isDisabled: false,
  },
})

export type TCardVariantProps = VariantProps<typeof cardVariants>

export type TCardSlots = keyof ReturnType<typeof cardVariants>

export type TCardReturnType = ReturnType<typeof cardVariants>
